// @ts-ignore
import { v4 as uuidv4 } from 'uuid';
import { LAST_ORDER_DATE_NO_LATER_THAN } from '../config';
import {
  IContactRepMethod,
  IOrder,
  IProject,
  ISortedOrders,
  SortedProducts,
} from '../types';

const hasContactMethodForBrandAndProject = (
  contactRepMethod: IContactRepMethod | '',
  brandId: string,
  projectId: string,
) => {
  return (
    Array.isArray(contactRepMethod) &&
    contactRepMethod.some(
      (method) =>
        method.brand_id === brandId && method.project_id === projectId,
    )
  );
};

const isOrderAfterDate = (orderDate: string, cutOffDate: number) => {
  return new Date(orderDate).getTime() >= new Date(cutOffDate).getTime();
};

const tenDaysAgo = () => {
  const today = new Date();
  return new Date(today.setDate(today.getDate() - 10));
};

const isToday = (date: string) => {
  const isSameDate = (d1: Date, d2: Date) =>
    d1.getDate() === d2.getDate() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getFullYear() === d2.getFullYear();
  return isSameDate(new Date(date), new Date());
};

interface SortOrders {
  orders: IOrder[];
  showTodayOrders?: boolean;
  lastOrderCutoff?: number;
}

export const sortOrders = (args: SortOrders): ISortedOrders => {
  const {
    orders,
    showTodayOrders = false,
    lastOrderCutoff = LAST_ORDER_DATE_NO_LATER_THAN,
  } = args;
  const productsSorted: SortedProducts = {};
  const projectList: IProject[] = [];
  const projectIds = new Set<string>();

  for (const {
    entity_id: orderId,
    contact_rep_method,
    created_at,
    products: productList,
  } of orders) {
    const date = created_at.split(' ')[0];

    // If we're not showing today's orders and the order was today, skip it.
    if (isToday(date) && !showTodayOrders) {
      continue;
    }

    for (const product of productList) {
      const {
        brand: brandName,
        manufacturer_id: brandId,
        project,
        assigned_rep,
        assignable_reps,
      } = product;
      // all project names have &#39; instead of ' so we need to replace it
      const projectName = project.name.replace(/&#39;/g, "'");
      const updatedProject: IProject = { ...project, name: projectName };
      const projectId = updatedProject.id;

      // Check if the product is not assignable or has been assigned
      if (
        !projectId ||
        (!assigned_rep?.id && !assignable_reps) ||
        assignable_reps.length === 0
      ) {
        continue;
      }

      // Check if the product brand is active
      if (product.brand_status === 0) {
        continue;
      }

      // products that are out of stock should be hidden from the modal window if less than 10 days have passed since the order was placed
      if (
        parseInt(product.product_qty_before_order) <= 1 &&
        isOrderAfterDate(date, tenDaysAgo().getTime())
      ) {
        continue;
      }

      // Check contact method or order date
      if (
        (contact_rep_method &&
          hasContactMethodForBrandAndProject(
            contact_rep_method,
            brandId,
            projectId,
          )) ||
        !isOrderAfterDate(date, lastOrderCutoff)
      ) {
        continue;
      }

      // Add to projects if not already present
      if (!projectIds.has(projectId)) {
        projectList.push(updatedProject);
        projectIds.add(projectId);
      }

      const productInfo = {
        products: [product],
        project: updatedProject,
        itemID: uuidv4(),
        order_id: orderId,
      };
      if (!productsSorted[projectName]) {
        productsSorted[projectName] = {};
      }
      if (!productsSorted[projectName][brandName]) {
        productsSorted[projectName][brandName] = {};
      }
      if (!productsSorted[projectName][brandName][date]) {
        productsSorted[projectName][brandName][date] = productInfo;
      } else {
        productsSorted[projectName][brandName][date].products.push(product);
      }
    }
  }

  return { products: productsSorted, projects: projectList };
};
